import { ApplicationRef, ComponentRef, Injectable, Injector, OnDestroy } from "@angular/core";
import { createComponent } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { DsBottomSheetComponent } from "../components/ds-bottom-sheet/ds-bottom-sheet.component";
import { BottomSheetButton, BottomSheetConfig, BottomSheetRow } from "../components/types/bottom-sheet.types";

@Injectable({ providedIn: "root" })
export class DsBottomSheetService implements OnDestroy {
  private componentRef?: ComponentRef<DsBottomSheetComponent>;
  private routerSubscription?: Subscription;

  constructor(private appRef: ApplicationRef, private injector: Injector, private router: Router) {
    // Close only when navigation actually completes. Using NavigationStart would run
    // before CanDeactivate guards (e.g. unsaved quote modal), closing the sheet while
    // the user may still cancel and stay on the page.
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.close();
      });
  }

  open(config: BottomSheetConfig) {
    if (this.componentRef) this.close();

    const componentRef = createComponent(DsBottomSheetComponent, {
      environmentInjector: this.appRef.injector as any,
      elementInjector: this.injector,
    });

    componentRef.instance.title = config.title;
    componentRef.instance.buttons = config.buttons;
    componentRef.instance.service = true;
    componentRef.instance.src = config.src;
    componentRef.instance.description = config.description;
    componentRef.instance.fullScreen = config.fullScreen ?? true;
    componentRef.instance.closeOnAction = config.closeOnAction ?? true;
    componentRef.instance.persistent = config.persistent ?? false;
    componentRef.instance.buttonsAlign = config.buttonsAlign ?? "center";
    componentRef.instance.rows = config.rows ?? [];
    componentRef.instance.closeFn = () => this.close();

    this.appRef.attachView(componentRef.hostView);
    document.body.appendChild(componentRef.location.nativeElement);

    this.componentRef = componentRef;
  }

  /**
   * Update bottom-sheet rows (when opened with config.rows).
   * Use this to refresh buttons or priority without closing the sheet.
   */
  updateRows(rows: BottomSheetRow[]): void {
    if (this.componentRef) {
      this.componentRef.instance.rows = rows;
      this.componentRef.changeDetectorRef.detectChanges();
    }
  }

  updateButtons(buttons: BottomSheetButton[]): void {
    if (this.componentRef) {
      this.componentRef.instance.buttons = buttons;
      this.componentRef.changeDetectorRef.detectChanges();
    }
  }
  close() {
    if (!this.componentRef) return;
    this.appRef.detachView(this.componentRef.hostView);
    this.componentRef.destroy();
    this.componentRef = undefined;
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.close();
  }
}
